import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  SafeAreaView,
  Image,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Modal,
  InteractionManager,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import QRCode from 'react-native-qrcode-svg';
import {
  getSpinningWheelClaimHistory,
  postSpinWheelClaimProduct,
} from '../services/spinningWheelApi';
import { getShippingAddresses } from '../services/shippingAddressApi';
import SpinWheelShippingAddressModal from '../components/SpinWheelShippingAddressModal';
import { getSpinProductDisplayLines } from '../utils/spinProductDisplay';
import AppHeader from '../components/AppHeader';

const MONTHS_SHORT_ID = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
const HISTORY_PAGE_SIZE = 5;

function formatSpinClaimTs(tsSec) {
  const d = new Date(tsSec * 1000);
  const day = d.getDate();
  const mon = MONTHS_SHORT_ID[d.getMonth()] ?? '';
  const y = d.getFullYear();
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${mon} ${y}, ${h}:${m}`;
}

export default function SpinPrizeClaimHistoryScreen({ navigation }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [claimBusyId, setClaimBusyId] = useState(null);
  const [addrModalVisible, setAddrModalVisible] = useState(false);
  const [claimConfirm, setClaimConfirm] = useState(null);
  const [boothConfirm, setBoothConfirm] = useState(null);
  const [reclaimTargetId, setReclaimTargetId] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [redemptionView, setRedemptionView] = useState(null);

  const isOfflineEvent = (item) =>
    String(item?.fulfillment || '').toLowerCase() === 'offline_event';
  const [visibleCount, setVisibleCount] = useState(HISTORY_PAGE_SIZE);

  const loadHistory = useCallback(async () => {
    try {
      setError(null);
      const userToken = await AsyncStorage.getItem('userToken');
      const sessionId = await AsyncStorage.getItem('session_id');
      if (!userToken || !sessionId) {
        navigation.navigate('Login');
        return;
      }
      const data = await getSpinningWheelClaimHistory();
      setItems(Array.isArray(data?.items) ? data.items : []);
      setVisibleCount(HISTORY_PAGE_SIZE);
    } catch (e) {
      console.warn('Spin claim history:', e?.message);
      setError(e?.message || 'Gagal memuat data');
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigation]);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const userToken = await AsyncStorage.getItem('userToken');
        const sessionId = await AsyncStorage.getItem('session_id');
        if (!userToken || !sessionId) {
          navigation.navigate('Login');
          setLoading(false);
          return;
        }
        await loadHistory();
      } catch (_) {
        setLoading(false);
      }
    };
    bootstrap();
  }, [navigation, loadHistory]);

  const onRefresh = () => {
    setRefreshing(true);
    loadHistory();
  };

  const runClaim = useCallback(async (logId, idAlamat) => {
    if (!logId) return;
    setClaimBusyId(logId);
    try {
      const idUse = idAlamat != null && idAlamat > 0 ? idAlamat : undefined;
      const claimData = await postSpinWheelClaimProduct(logId, idUse);
      const fulfillment = claimData?.fulfillment === 'offline_event' ? 'offline_event' : 'shipping';
      const redemption = claimData?.redemption_code ? String(claimData.redemption_code) : '';
      const already = !!claimData?.already_claimed;
      const noTrx = claimData?.no_transaksi ? String(claimData.no_transaksi) : '';

      let message = '';
      if (fulfillment === 'offline_event') {
        message = already
          ? 'Hadiah booth sudah diklaim sebelumnya.'
          : 'Tunjukkan kode redemption di bawah ke panitia.';
      } else {
        message = already
          ? `Hadiah sudah diklaim.${noTrx ? `\nNomor pesanan: ${noTrx}` : ''}`
          : `Hadiah berhasil diklaim.${noTrx ? `\nNomor pesanan: ${noTrx}` : ''}`;
      }
      setBoothConfirm(null);
      setClaimConfirm(null);
      const modalPayload = { message, fulfillment, redemption_code: redemption };
      InteractionManager.runAfterInteractions(() => {
        setTimeout(() => setSuccessModal(modalPayload), 120);
      });
      await loadHistory();
    } catch (err) {
      setBoothConfirm(null);
      const msg = err?.message || 'Klaim gagal';
      const needAddr = /alamat/i.test(msg) || /pengiriman/i.test(msg) || /lengkapi/i.test(msg);
      if (needAddr) {
        Alert.alert('Perhatian', msg, [
          { text: 'Nanti', style: 'cancel' },
          { text: 'Isi alamat', onPress: () => setAddrModalVisible(true) },
        ]);
      } else {
        Alert.alert('Spinning Wheel', msg);
      }
    } finally {
      setClaimBusyId(null);
      setReclaimTargetId(null);
      setClaimConfirm(null);
    }
  }, [loadHistory]);

  const startReclaim = useCallback(async (item) => {
    const logId = item?.spin_log_id || item?.id;
    if (!logId || !item?.can_reclaim) return;

    setReclaimTargetId(logId);

    if (isOfflineEvent(item)) {
      setBoothConfirm({
        logId,
        productName: getSpinProductDisplayLines(item).displayName,
      });
      return;
    }

    try {
      const list = await getShippingAddresses();
      const primary = list.find((a) => Number(a.alamat_utama) === 1);
      if (!primary) {
        setAddrModalVisible(true);
        return;
      }
      setClaimConfirm({ addr: primary, logId });
    } catch (e) {
      Alert.alert('Spinning Wheel', e?.message || 'Gagal memeriksa alamat');
      setReclaimTargetId(null);
    }
  }, [runClaim]);

  const onShippingAddressSaved = useCallback(
    (addr) => {
      setAddrModalVisible(false);
      const lid = reclaimTargetId;
      const aid = addr && typeof addr.id === 'number' ? addr.id : 0;
      if (lid && aid > 0) {
        void runClaim(lid, aid);
      }
    },
    [reclaimTargetId, runClaim],
  );

  const renderThumb = (item) => {
    if (item.prize_type === 'product') {
      if (item.image_url) {
        return (
          <View style={styles.thumbWrap}>
            <Image source={{ uri: item.image_url }} style={styles.thumb} resizeMode="contain" />
          </View>
        );
      }
      return (
        <View style={styles.thumbWrap}>
          <Ionicons name="gift" size={28} color="#F59E0B" />
        </View>
      );
    }
    return (
      <View style={styles.thumbWrap}>
        <View style={styles.thumbPlaceholder}>
          <Ionicons name="star" size={22} color="#F59E0B" />
        </View>
      </View>
    );
  };

  const renderItem = ({ item }) => {
    const isExpired = item.claim_status === 'expired' || item.status_expired === true;
    const isUnclaimed = item.claim_status === 'unclaimed' && item.can_reclaim;
    const busy = claimBusyId === (item.spin_log_id || item.id);

    const statusStyle = isExpired
      ? styles.statusExpired
      : isUnclaimed
        ? styles.statusPending
        : styles.statusClaimed;

    const { displayName } = getSpinProductDisplayLines(item);

    return (
      <View style={styles.card}>
        <View style={styles.row}>
          {renderThumb(item)}
          <View style={styles.textCol}>
            <Text style={styles.titleLine}>
              {displayName}
            </Text>
            {item.nama_program ? (
              <Text style={styles.programLine}>{item.nama_program}</Text>
            ) : null}
            <Text
              style={[
                styles.statusLine,
                statusStyle,
              ]}
            >
              {item.status_label}
            </Text>
            {item.claim_status === 'claimed' && item.no_transaksi ? (
              <Text style={styles.codeLine}>Pesanan: {item.no_transaksi}</Text>
            ) : null}
            <Text style={styles.dateLine}>{formatSpinClaimTs(item.created_at)}</Text>
          </View>
        </View>
        {isUnclaimed ? (
          <TouchableOpacity
            style={[styles.reclaimBtn, busy && styles.reclaimBtnBusy]}
            disabled={busy}
            onPress={() => void startReclaim(item)}
          >
            {busy ? (
              <ActivityIndicator color="#FFFFFF" size="small" />
            ) : (
              <Text style={styles.reclaimBtnText}>Klaim Hadiah</Text>
            )}
          </TouchableOpacity>
        ) : null}
        {item.claim_status === 'claimed'
          && isOfflineEvent(item)
          && item.redemption_code ? (
          <TouchableOpacity
            style={styles.redemptionLinkBtn}
            onPress={() => {
              const lines = getSpinProductDisplayLines(item);
              setRedemptionView({
                productName: lines.displayName,
                code: String(item.redemption_code),
              });
            }}
          >
            <Text style={styles.redemptionLinkText}>Lihat kode redemption</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  };

  const renderHeader = () => (
    <AppHeader title="Riwayat Klaim Hadiah" onBack={() => navigation.goBack()} />
  );

  if (loading && items.length === 0 && !error) {
    return (
      <SafeAreaView style={styles.safe}>
        {renderHeader()}
        <View style={styles.centered}>
          <ActivityIndicator size="large" color="#64748B" />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      {renderHeader()}
      {error && items.length === 0 ? (
        <View style={styles.centered}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity style={styles.retryBtn} onPress={() => { setLoading(true); loadHistory(); }}>
            <Text style={styles.retryBtnText}>Coba lagi</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <FlatList
          data={items.slice(0, visibleCount)}
          keyExtractor={(row) => String(row.id)}
          renderItem={renderItem}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          ListEmptyComponent={
            <View style={styles.emptyWrap}>
              <Text style={styles.emptyText}>Belum ada riwayat klaim hadiah.</Text>
            </View>
          }
          ListFooterComponent={
            items.length > 0 ? (
              <View style={styles.listFooter}>
                {visibleCount < items.length ? (
                  <TouchableOpacity
                    style={styles.loadMoreBtn}
                    onPress={() =>
                      setVisibleCount((c) => Math.min(c + HISTORY_PAGE_SIZE, items.length))
                    }
                  >
                    <Text style={styles.loadMoreBtnText}>Lihat Lebih Banyak</Text>
                  </TouchableOpacity>
                ) : items.length > HISTORY_PAGE_SIZE ? (
                  <Text style={styles.allShownText}>Semua data telah ditampilkan.</Text>
                ) : null}
              </View>
            ) : null
          }
          contentContainerStyle={
            items.length === 0 ? styles.listEmptyContainer : styles.listContent
          }
        />
      )}

      <SpinWheelShippingAddressModal
        visible={addrModalVisible}
        onClose={() => {
          setAddrModalVisible(false);
          setReclaimTargetId(null);
        }}
        onSaved={onShippingAddressSaved}
      />

      <Modal
        animationType="fade"
        transparent
        visible={!!boothConfirm}
        onRequestClose={() => {
          setBoothConfirm(null);
          setReclaimTargetId(null);
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.confirmCard}>
            <Text style={styles.confirmTitle}>Klaim hadiah booth</Text>
            <Text style={styles.confirmSubtitle} numberOfLines={2}>
              {boothConfirm?.productName}
            </Text>
            <Text style={styles.confirmSubtitle}>
              Hadiah produk akan diklaim tanpa pengiriman. Anda akan mendapat kode redemption untuk
              validasi panitia.
            </Text>
            <TouchableOpacity
              style={[styles.reclaimBtn, claimBusyId === boothConfirm?.logId && styles.reclaimBtnBusy]}
              disabled={!!claimBusyId}
              onPress={() => {
                const lid = boothConfirm?.logId;
                if (lid) void runClaim(lid, null);
              }}
            >
              {claimBusyId === boothConfirm?.logId ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text style={styles.reclaimBtnText}>Klaim & dapat kode</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.confirmBack}
              onPress={() => {
                setBoothConfirm(null);
                setReclaimTargetId(null);
              }}
            >
              <Text style={styles.confirmBackText}>Batal</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent
        visible={!!claimConfirm}
        onRequestClose={() => setClaimConfirm(null)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.confirmCard}>
            <Text style={styles.confirmTitle}>Konfirmasi pengiriman</Text>
            <Text style={styles.confirmSubtitle}>
              Hadiah produk akan diproses dengan alamat utama berikut:
            </Text>
            <Text style={styles.confirmAddr}>
              {(claimConfirm?.addr?.nama_alamat || 'Utama')}
              {'\n'}
              {`${claimConfirm?.addr?.nama_penerima || ''} · ${claimConfirm?.addr?.no_telp || ''}`}
              {'\n'}
              {[
                claimConfirm?.addr?.detail_alamat,
                claimConfirm?.addr?.kecamatan,
                claimConfirm?.addr?.kota,
                claimConfirm?.addr?.provinsi,
                claimConfirm?.addr?.kodepos,
              ]
                .filter(Boolean)
                .join(', ')}
            </Text>
            <TouchableOpacity
              style={styles.reclaimBtn}
              disabled={!!claimBusyId}
              onPress={() => {
                const lid = claimConfirm?.logId;
                if (lid) void runClaim(lid, Number(claimConfirm?.addr?.id));
              }}
            >
              <Text style={styles.reclaimBtnText}>Ya, klaim ke alamat ini</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.confirmBack}
              onPress={() => setClaimConfirm(null)}
            >
              <Text style={styles.confirmBackText}>Batal</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent
        visible={!!successModal}
        onRequestClose={() => setSuccessModal(null)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.confirmCard}>
            <Text style={styles.confirmTitle}>Klaim berhasil</Text>
            <Text style={styles.confirmSubtitle}>{successModal?.message}</Text>
            {successModal?.fulfillment === 'offline_event' ? (
              <>
                <Text style={styles.redemptionLabel}>Tunjukkan QR ke panitia booth / event</Text>
                {successModal?.redemption_code ? (
                  <View style={{ alignItems: 'center', marginBottom: 12 }}>
                    <QRCode
                      value={String(successModal.redemption_code)}
                      size={180}
                      color="#000000"
                      backgroundColor="#FFFFFF"
                    />
                  </View>
                ) : null}
                <Text style={styles.redemptionLabel}>Kode redemption</Text>
                <Text style={styles.redemptionBig} selectable>
                  {successModal?.redemption_code || '—'}
                </Text>
              </>
            ) : null}
            <TouchableOpacity style={styles.reclaimBtn} onPress={() => setSuccessModal(null)}>
              <Text style={styles.reclaimBtnText}>Tutup</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent
        visible={!!redemptionView}
        onRequestClose={() => setRedemptionView(null)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.confirmCard}>
            <Text style={styles.confirmTitle}>Kode redemption</Text>
            <Text style={styles.confirmSubtitle} numberOfLines={2}>
              {redemptionView?.productName}
            </Text>
            <Text style={styles.redemptionLabel}>Tunjukkan QR ke panitia booth / event</Text>
            {redemptionView?.code ? (
              <View style={{ alignItems: 'center', marginBottom: 12 }}>
                <QRCode
                  value={String(redemptionView.code)}
                  size={200}
                  color="#000000"
                  backgroundColor="#FFFFFF"
                />
              </View>
            ) : null}
            <Text style={styles.redemptionLabel}>Kode redemption</Text>
            <Text style={styles.redemptionBig} selectable>
              {redemptionView?.code}
            </Text>
            <TouchableOpacity style={styles.reclaimBtn} onPress={() => setRedemptionView(null)}>
              <Text style={styles.reclaimBtnText}>Tutup</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  listContent: {
    padding: 16,
    paddingBottom: 24,
  },
  listEmptyContainer: {
    flexGrow: 1,
    padding: 16,
  },
  listFooter: {
    paddingTop: 8,
    paddingBottom: 24,
    alignItems: 'center',
  },
  loadMoreBtn: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#334155',
    backgroundColor: '#FFFFFF',
  },
  loadMoreBtnText: {
    fontSize: 14,
    fontFamily: 'Poppins-Medium',
    color: '#334155',
  },
  allShownText: {
    fontSize: 13,
    fontFamily: 'Poppins',
    color: '#64748B',
    textAlign: 'center',
  },
  card: {
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
    paddingVertical: 16,
    paddingHorizontal: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  thumbWrap: {
    width: 98,
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumb: {
    width: 50,
    height: 62,
  },
  thumbPlaceholder: {
    width: '100%',
    height: 62,
    borderRadius: 8,
    backgroundColor: '#FFFBEB',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#FDE68A',
  },
  textCol: {
    flex: 1,
    gap: 4,
    marginLeft: 12,
  },
  titleLine: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'Poppins-SemiBold',
    color: '#0F172A',
  },
  programLine: {
    fontSize: 12,
    fontFamily: 'Poppins',
    color: '#64748B',
  },
  statusLine: {
    fontSize: 13,
    fontWeight: '600',
    fontFamily: 'Poppins-SemiBold',
  },
  statusPending: {
    color: '#D97706',
  },
  statusClaimed: {
    color: '#00C16A',
  },
  statusExpired: {
    color: '#EF4444',
  },
  codeLine: {
    fontSize: 12,
    fontFamily: 'Poppins',
    color: '#475569',
  },
  dateLine: {
    fontSize: 12,
    fontFamily: 'Poppins',
    color: '#94A3B8',
  },
  reclaimBtn: {
    marginTop: 12,
    backgroundColor: '#0F172A',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  reclaimBtnBusy: {
    opacity: 0.7,
  },
  reclaimBtnText: {
    color: '#F8FAFC',
    fontFamily: 'Poppins-SemiBold',
    fontSize: 14,
    fontWeight: '600',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  emptyWrap: {
    paddingTop: 48,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#64748B',
    textAlign: 'center',
  },
  errorText: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 16,
  },
  retryBtn: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: '#0F172A',
    borderRadius: 8,
  },
  retryBtnText: {
    color: '#F8FAFC',
    fontFamily: 'Poppins-Medium',
    fontSize: 14,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  confirmCard: {
    width: '100%',
    maxWidth: 320,
    alignSelf: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
  },
  confirmTitle: {
    fontSize: 17,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  confirmSubtitle: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#475569',
    lineHeight: 22,
    marginBottom: 16,
  },
  confirmAddr: {
    fontSize: 13,
    fontFamily: 'Poppins',
    color: '#334155',
    lineHeight: 20,
    marginBottom: 16,
    backgroundColor: '#F8FAFC',
    padding: 12,
    borderRadius: 8,
  },
  confirmBack: {
    marginTop: 10,
    alignItems: 'center',
    paddingVertical: 8,
  },
  confirmBackText: {
    fontSize: 14,
    color: '#64748B',
    fontFamily: 'Poppins',
  },
  redemptionLabel: {
    fontSize: 13,
    fontFamily: 'Poppins',
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 8,
  },
  redemptionBig: {
    fontSize: 20,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '700',
    letterSpacing: 1,
    textAlign: 'center',
    color: '#0F172A',
    backgroundColor: '#F8FAFC',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: '#CBD5E1',
    borderRadius: 10,
    padding: 14,
    marginBottom: 12,
    width: '100%',
  },
  redemptionLinkBtn: {
    marginTop: 10,
    paddingVertical: 10,
    alignItems: 'center',
  },
  redemptionLinkText: {
    fontSize: 14,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
    color: '#0369A1',
    textDecorationLine: 'underline',
  },
});
